import { useStreamingReport } from '../../hooks/useStreamingReport';
import StreamingStatus from '../../components/StreamingStatus';
import React, { useEffect } from 'react';
import { Button, Form, Select, ConfigProvider, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import '../../App.css';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import FourPillars from '../../components/FourPillars';
import AILoadingAnimation from '../../components/AILoadingAnimation';
import { CITY_OPTIONS, cityFilterOption, type City } from '../../data/cities';
import { calculateDaYunWithAge, type DaYunWithAge } from '../../utils';
import { useExportImage } from '../../hooks/useExportImage';

dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

interface FormValues {
  birthDateTime: {
    date: dayjs.Dayjs;
    isLunar: boolean;
  };
  gender: 'male' | 'female';
  city?: string;
  longitude: number;
  latitude?: number;
  distinguishZiShi?: boolean;
  relationshipStatus?: string;
  relationshipBackground?: string;
  marriageQuestion?: string;
}

function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
  const n = datetime.dayOfYear();
  const B = (2 * Math.PI * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const zoneLongitude = 15 * timeZoneOffset;
  const deltaLon = 4 * (longitude - zoneLongitude);
  const deltaMinutes = eot + deltaLon;
  return datetime.add(deltaMinutes, 'minute');
}

export const MarriagePage = () => {
  const [form] = Form.useForm();
  const reportRef = React.useRef<HTMLDivElement | null>(null);
  const { exportImage } = useExportImage('婚姻专题分析报告');

  const [fourPillars, setFourPillars] = React.useState<{ year: string; month: string; day: string; hour: string } | null>(null);
  const [daYun, setDaYun] = React.useState<DaYunWithAge[]>([]);
  const [lunarDateStr, setLunarDateStr] = React.useState<string>('');
  const [trueSolarTimeStr, setTrueSolarTimeStr] = React.useState<string>('');

  const [aiLoading, setAiLoading] = React.useState(false);
  const { run: runReportStream, streaming: reportStreaming, error: streamError } = useStreamingReport();
  const [marriageAnswer, setMarriageAnswer] = React.useState<string>('');
  const reportHasContent = Boolean(marriageAnswer);

  const cityOptions = React.useMemo(() => CITY_OPTIONS, []);

  const handleCityChange = (_cityName: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      longitude: selectedCity.longitude,
      latitude: selectedCity.latitude,
    });
  };

  const performPaiPan = (birthInfo: {
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
    solar?: string;
    longitude: number;
    gender: 'male' | 'female';
    distinguishZiShi?: boolean;
  }) => {
    let inputYear: number;
    let inputMonth: number;
    let inputDay: number;
    let inputHour: number;
    let inputMinute: number;

    if (birthInfo.solar) {
      const solarDate = dayjs(birthInfo.solar);
      inputYear = solarDate.year();
      inputMonth = solarDate.month() + 1;
      inputDay = solarDate.date();
      inputHour = solarDate.hour();
      inputMinute = solarDate.minute();
    } else {
      inputYear = birthInfo.year!;
      inputMonth = birthInfo.month!;
      inputDay = birthInfo.day!;
      inputHour = birthInfo.hour!;
      inputMinute = birthInfo.minute!;
    }

    const { longitude, gender, distinguishZiShi = false } = birthInfo;
    const standard = dayjs(
      `${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay
        .toString()
        .padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute
          .toString()
          .padStart(2, '0')}:00`,
    );
    const trueSolar = toTrueSolarTimeWithMinutes(standard, longitude, 8);

    let standardYear = standard.year();
    let standardMonth = standard.month() + 1;
    let standardDay = standard.date();
    const standardHour = standard.hour();
    const standardMinute = standard.minute();

    const isInZiShi = standardHour === 23 || standardHour === 0;
    if (isInZiShi && !distinguishZiShi && standardHour === 23) {
      const nextDay = standard.add(1, 'day');
      standardYear = nextDay.year();
      standardMonth = nextDay.month() + 1;
      standardDay = nextDay.date();
    }

    const year = trueSolar.year();
    const month = trueSolar.month() + 1;
    const day = trueSolar.date();
    const trueSolarHour = trueSolar.hour();
    const trueSolarMinute = trueSolar.minute();

    setTrueSolarTimeStr(trueSolar.format('YYYY年MM月DD日 HH:mm'));

    const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
    const lunarDayStandard = solarDayStandard.getLunarDay();
    const solarObj = Solar.fromDate(standard.toDate());
    const lunarObj = solarObj.getLunar();

    const solarHourTrue = SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
    const lunarHourTrue = solarHourTrue.getLunarHour();

    const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
    let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
    const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
    const sixtyCycleHour = lunarHourTrue.getSixtyCycle().getName();

    try {
      const currentJieQi = lunarObj.getCurrentJieQi();

      if (currentJieQi && currentJieQi.isJie()) {
        const jieQiSolar = currentJieQi.getSolar();
        const birthTimestamp = standard.valueOf();
        const jieQiTimestamp = new Date(
          jieQiSolar.getYear(),
          jieQiSolar.getMonth() - 1,
          jieQiSolar.getDay(),
          jieQiSolar.getHour(),
          jieQiSolar.getMinute(),
          jieQiSolar.getSecond(),
        ).getTime();

        if (birthTimestamp < jieQiTimestamp) {
          const prevJieQi = lunarObj.getPrevJieQi();
          if (prevJieQi) {
            const prevJieQiSolar = prevJieQi.getSolar();
            const prevSolar = Solar.fromYmd(
              prevJieQiSolar.getYear(),
              prevJieQiSolar.getMonth(),
              prevJieQiSolar.getDay(),
            );
            sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
          }
        }
      }
    } catch (error) {
      console.error('月柱节气精确判断失败，使用默认值:', error);
    }

    setLunarDateStr(
      `${lunarObj.getYearInGanZhiExact()}(${lunarObj.getYearShengXiaoExact()})年 ${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()} ${lunarHourTrue
        .getSixtyCycle()
        .getName()}时`,
    );

    setFourPillars({ year: sixtyCycleYear, month: sixtyCycleMonth, day: sixtyCycleDay, hour: sixtyCycleHour });

    const daYunList = calculateDaYunWithAge(
      standardYear,
      standardMonth,
      standardDay,
      standardHour,
      standardMinute,
      sixtyCycleYear,
      sixtyCycleMonth,
      gender,
      8,
    );
    setDaYun(daYunList);
  };

  const onFinish = async (values: FormValues) => {
    const { birthDateTime, longitude, gender, distinguishZiShi } = values;
    if (!birthDateTime) {
      message.error('请选择出生日期和时间');
      return;
    }

    const { date } = birthDateTime;
    const longitudeNum = Number(longitude);
    if (Number.isNaN(longitudeNum)) {
      message.error('请输入有效的经度，例如 116.4');
      return;
    }

    const standard = date.second(0).millisecond(0);
    performPaiPan({
      year: standard.year(),
      month: standard.month() + 1,
      day: standard.date(),
      hour: standard.hour(),
      minute: standard.minute(),
      longitude: longitudeNum,
      gender,
      distinguishZiShi: distinguishZiShi || false,
    });

    setMarriageAnswer('');
  };

  const generateMarriageReport = async () => {
    if (!fourPillars || daYun.length === 0) {
      message.error('请先完成排盘');
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, relationshipStatus, relationshipBackground, marriageQuestion } = values;

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city,
      longitude,
      latitude,
      gender,
      relationshipStatus: relationshipStatus || '',
      relationshipBackground: relationshipBackground || '',
      marriageQuestion: marriageQuestion || '',
    };

    setAiLoading(true);
    try {
      const resp = await runReportStream('/api/marriage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          birthInfo,
          relationshipStatus: relationshipStatus || '',
          relationshipBackground: relationshipBackground || '',
          marriageQuestion: marriageQuestion || '',
        }),
      }, (text) => { setMarriageAnswer(text); setAiLoading(false); });

      if (!resp.ok) {
        let errorMessage = '婚姻专题报告生成失败';
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // ignore
        }
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      setMarriageAnswer(data.answer || '暂无报告内容');
      message.success('婚姻专题报告已生成');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '生成失败，请稍后重试');
    } finally {
      setAiLoading(false);
    }
  };

  const onReset = () => {
    form.resetFields();
    setFourPillars(null);
    setDaYun([]);
    setLunarDateStr('');
    setTrueSolarTimeStr('');
    setMarriageAnswer('');
  };

  useEffect(() => {
    form.setFieldsValue({
      birthDateTime: { date: dayjs(), isLunar: false },
      gender: '',
    });
  }, [form]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [reportHasContent]);

  return (
    <ConfigProvider locale={zhCN}>
      <AILoadingAnimation visible={aiLoading} />
      <div className="app-container">
        <div className="app-layout">
          <div className="center-content">
            <div style={{ width: '100%', maxWidth: '900px' }}>
              <div className="form-card">
                <h1 className="form-title">婚姻专题分析</h1>
                <h2 className="hero-subtitle">基于八字结构，拆解缘分、成婚时机与婚后经营重点</h2>
                <Form form={form} name="marriage-form" onFinish={onFinish} layout="vertical">
                  <Form.Item
                    name="birthDateTime"
                    label="出生时间"
                    rules={[{ required: true, message: '请选择出生时间' }]}
                  >
                    <BirthDateTimePicker />
                  </Form.Item>

                  <Form.Item
                    name="gender"
                    label="性别"
                    rules={[{ required: true, message: '请选择性别' }]}
                  >
                    <Select
                      placeholder="请选择性别"
                      options={[
                        { value: 'male', label: '男' },
                        { value: 'female', label: '女' },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="city"
                    label="出生城市"
                    rules={[{ required: true, message: '请选择出生城市' }]}
                  >
                    <Select
                      showSearch
                      placeholder="请选择城市"
                      options={cityOptions}
                      onChange={handleCityChange}
                      optionFilterProp="searchText"
                      filterOption={cityFilterOption}
                    />
                  </Form.Item>

                  <Form.Item name="longitude" hidden>
                    <input type="number" />
                  </Form.Item>

                  <Form.Item
                    name="relationshipStatus"
                    label="当前情感状态"
                    tooltip="用于判断报告重点，例如单身、恋爱中、已婚、异地、离异后重启等"
                  >
                    <input
                      type="text"
                      placeholder="例如：单身；恋爱中但不稳定；已婚三年；异地恋；离婚后想再看姻缘"
                      style={inputStyle}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </Form.Item>

                  <Form.Item
                    name="relationshipBackground"
                    label="补充背景"
                    tooltip="补充现实情况，越具体越方便做婚姻专题判断"
                  >
                    <textarea
                      placeholder="例如：家里催婚，但自己挑人很慢；目前有对象，双方在谈结婚但现实阻力大；婚后常因沟通和原生家庭闹矛盾..."
                      rows={3}
                      style={textareaStyle}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </Form.Item>

                  <Form.Item
                    name="marriageQuestion"
                    label="最想重点问的问题"
                    tooltip="可以直接问成婚时机、对象特征、婚后风险、是否适合推进关系等"
                  >
                    <textarea
                      placeholder="例如：什么时候比较容易遇到正缘？这段关系能不能推进到结婚？婚后最大的矛盾点会是什么？"
                      rows={3}
                      style={textareaStyle}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </Form.Item>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <Button type="primary" htmlType="submit" className="submit-button">
                      开始排盘
                    </Button>
                    <Button htmlType="button" onClick={onReset} className="reset-button">
                      重置
                    </Button>
                  </div>
                </Form>
              </div>

              {fourPillars && (
                <div className="result-card" style={{ marginTop: '24px' }}>
                  <h2 className="result-title">排盘结果</h2>
                  <div style={{ textAlign: 'center', marginBottom: 20, color: '#666' }}>
                    <p>公历（北京时间）：{form.getFieldValue('birthDateTime')?.date?.format('YYYY年MM月DD日 HH:mm')}</p>
                    <p style={{ color: '#d4af6a', fontWeight: 500, fontSize: '14px', margin: '8px 0' }}>真太阳时：{trueSolarTimeStr}</p>
                    <p>农历：{lunarDateStr}</p>
                  </div>
                  <FourPillars {...fourPillars} />

                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button type="primary" size="large" className="submit-button" disabled={reportStreaming} onClick={generateMarriageReport}>
                      生成我的婚姻专题报告
                    </Button>
                  </div>
                </div>
              )}

              <StreamingStatus active={reportStreaming && Boolean(marriageAnswer)} error={streamError} />
              {marriageAnswer && (
                <div ref={reportRef} className="result-card" style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <div className="marriage-report-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 className="result-title" style={{ margin: 0 }}>婚姻专题分析报告</h2>
                      <Button
                        type="primary"
                        disabled={reportStreaming || Boolean(streamError)} onClick={() => exportImage('.marriage-report-content')}
                        style={saveButtonStyle}
                      >
                        🖼️ 保存为图片
                      </Button>
                    </div>

                    <div style={saveHintStyle}>
                      <div style={{ fontSize: '24px' }}>💡</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#d4af6a', marginBottom: '4px' }}>
                          报告内容偏长，建议先保存再慢慢看
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          保存后更方便对照时间节点、关系风险和经营建议反复查看
                        </div>
                      </div>
                    </div>

                    <div className="markdown-content" style={{ color: '#2f2f2f' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{marriageAnswer}</ReactMarkdown>
                    </div>

                    <div style={footerBoxStyle}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>💍</div>
                      <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
                        报告更适合结合现实节奏去看
                      </h3>
                      <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.7', marginBottom: '20px' }}>
                        婚姻专题不是替你做决定，而是帮你看清结构、时机和风险点。<br />
                        保存下来，后面推进关系时更容易拿来对照。
                      </p>
                      <Button
                        type="primary"
                        size="large"
                        disabled={reportStreaming || Boolean(streamError)} onClick={() => exportImage('.marriage-report-content')}
                        style={saveButtonStyle}
                      >
                        🖼️ 保存完整报告
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d9d9d9',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.3s',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const saveButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
  border: 'none',
  fontWeight: 600,
};

const saveHintStyle: React.CSSProperties = {
  margin: '0 0 20px',
  padding: '14px 18px',
  background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.08) 100%)',
  borderRadius: '10px',
  border: '1px solid rgba(212, 175, 106, 0.3)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const footerBoxStyle: React.CSSProperties = {
  marginTop: '40px',
  padding: '32px 24px',
  background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.08) 0%, rgba(212, 175, 106, 0.03) 100%)',
  borderRadius: '16px',
  border: '2px solid rgba(212, 175, 106, 0.2)',
  textAlign: 'center',
};

const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.currentTarget.style.borderColor = '#d4af6a';
};

const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.currentTarget.style.borderColor = '#d9d9d9';
};
